// FILE PATH: eRFQFrontend/src/component/Vendor/LogisticBidSubmission/LogisticPreview.jsx

import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Modal,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ErrorModal from "../../../utils/ErrorModal";
import { handleExportPDF } from "../../../utils/exportToPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const LogisticPreview = ({ rfq, items, setStep, storedUser }) => {
  const navigate = useNavigate();
  const { logisticDraftId } = useParams();
  const vendorId = JSON.parse(localStorage.getItem("vendor"));

  const pdfRef = useRef();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const formatAmount = (value) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(value || 0));

  const safeItems = Array.isArray(items) ? items : [];
  const grandTotal = safeItems.reduce(
    (sum, item) => sum + parseFloat(item.total || 0),
    0,
  );

  // FIX: was /api/logistics-quote → correct endpoint is /logistic/quote
  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/logistic/quote`,
        {
          vendorId,
          logisticDraftId,
          quoteItems: safeItems.map((item, idx) => ({
            srNo: idx + 1,
            particulars: item.particulars,
            unit: item.unit,
            quantity: item.quantity,
            currency: item.currency,
            rate: item.rate,
            total: item.total,
            remark: item.remark,
          })),
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setOpen(false);
      navigate("/vendor/logistic-dashboard");
    } catch (error) {
      console.error(error);
      setErrorMsg(error?.response?.data?.Exception || "Submission failed");
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {exporting && (
        <div className="uploading-div">
          Exporting PDF... <CircularProgress color="#fff" />
        </div>
      )}
      {loading && (
        <div className="loading-div uploading-div">
          Submitting Quote... <CircularProgress color="#fff" />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          className="btn-contained"
          sx={{ width: "200px" }}
          onClick={async () => {
            try {
              setExporting(true);
              await handleExportPDF(pdfRef, "Logistic_Quote");
            } catch (error) {
              console.log(error);
            } finally {
              setExporting(false);
            }
          }}
        >
          Export PDF
        </Button>
      </div>

      <h2>Quotation Submission Review</h2>

      <div ref={pdfRef}>
        <Box className="dashboard-container" px={10}>
          <Typography className="form-title">Logistics RFQ Details</Typography>
          <Grid2 container>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderTop: "1px solid #000" }}
            >
              Logistics RFQ ID
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{
                borderTop: "1px solid #000",
                borderRight: "1px solid #000",
              }}
            >
              {rfq?.logisticDraftId || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Mode of Consignment
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.modeOfConsignment || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Consignee
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.consignee || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Invoice No
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.invoiceNo || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Transit Time
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.transitTime || "-"} Days
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              No of Pallets
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.noOfPallets || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Origin
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.origin || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Destination
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.destination || "-"}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Gross Weight
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.grossWeight || "-"} Kg
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Length
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.length || "-"} inches
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Breadth
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.breadth || "-"} inches
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Height
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.height || "-"} inches
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Volume CBM
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.volumeCBM || "-"} CBM
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }} className="preview-grid">
              Address
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 6 }}
              className="preview-grid"
              sx={{ borderRight: "1px solid #000" }}
            >
              {rfq?.address || "-"}
            </Grid2>
          </Grid2>
        </Box>

        <Box className="dashboard-container" px={10}>
          <Typography className="form-title">Work Item Details</Typography>
          <table
            border={1}
            style={{
              borderCollapse: "collapse",
              width: "100%",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ padding: "20px" }}>Sr</th>
                <th>Particulars</th>
                <th>UOM</th>
                <th>Qty</th>
                <th>Currency</th>
                <th>Ex. Rate</th>
                <th>Rate</th>
                <th>Remark</th>
                <th>Total (in INR)</th>
              </tr>
            </thead>
            <tbody>
              {safeItems.map((item, idx) => (
                <tr key={idx} style={{ textAlign: "center" }}>
                  <td style={{ padding: "20px" }}>{idx + 1}</td>
                  <td>{item.particulars}</td>
                  <td>{item.unit}</td>
                  <td>{item.quantity}</td>
                  <td>{item.currency}</td>
                  <td>{item.exchangeRate}</td>
                  <td>{formatAmount(item.rate)}</td>
                  <td
                    style={{
                      maxWidth: "100px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.remark || "-"}
                  </td>
                  <td>{formatAmount(item.total)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold" }}>
                <td colSpan={8} style={{ textAlign: "right", padding: "20px" }}>
                  Grand Total (INR):
                </td>
                <td style={{ textAlign: "center" }}>
                  {formatAmount(grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={() => setStep(2)}>
          Back
        </Button>
        <Button className="btn-contained" onClick={() => setOpen(true)}>
          Submit
        </Button>
      </Box>

      <Modal open={open} onClose={() => !loading && setOpen(false)}>
        <Box sx={style}>
          <Typography variant="h6">
            Are you sure you want to submit these details?
          </Typography>
          <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
            <Button className="btn-outlined" onClick={() => setOpen(false)}>
              No
            </Button>
            <Button
              className="btn-contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Yes"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default LogisticPreview;
